import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useToast } from '../components/Toast';
import { Plus, Download, Save, RefreshCw, ChevronRight, ChevronDown, AlertTriangle, X, ExternalLink, Paperclip, RotateCcw, Pencil, Search } from 'lucide-react';
import Tooltip from '../components/Tooltip';
import { sourceLabel } from '../components/JournalEntryPopup';
import { useHighlightTarget } from '../hooks/useHighlightTarget';
import { useAuth } from '../contexts/AuthContext';
import { tr } from '../lib/i18nHelpers';
import { stageOf } from '../lib/lifecycleStepper';
import { filterLeafAccounts, stemOfCode, normalSide } from '../lib/coa-hierarchy';
import DropdownSelect from '../components/DropdownSelect';
import JournalEntryPopup from '../components/JournalEntryPopup';
import { useDateFilter } from '../contexts/DateFilterContext';
import PnlFormulaBanner from '../components/PnlFormulaBanner';
import { exportPlXlsx } from '../lib/export-pl-xlsx';
import DocumentPickerModal, { type PickedFile } from '../components/DocumentPickerModal';
import AutoGenerateSuggestionPanel from '../components/AutoGenerateSuggestionPanel';
import LifecycleStepper from '../components/LifecycleStepper';
import { useTableControls, Th, TablePager, TABLE_FETCH_CAP, ColumnMeta } from '../components/table';

// GJE list client-side sort/filter columns. Only fields with a single backing
// value on the entry object: Source (voucher prefix) and Created by (nested
// object) have no flat field, so their headers stay plain.
const COLS: ColumnMeta[] = [
  { key: 'entry_number', type: 'text' },
  { key: 'entry_date', type: 'date' },
  { key: 'description', type: 'text' },
  { key: 'total_debit', type: 'number' },
  { key: 'total_credit', type: 'number' },
  { key: 'status', type: 'select', selectValues: ['booked', 'posted', 'reconciled'] },
];

/**
 * Typeable COA account picker for the GJE filter bar: type a code or an
 * account name to narrow the suggestions, click (or arrow-keys + Enter) to
 * pick, or type a code manually. Reports the raw text upward; the caller
 * resolves it to an account code.
 */
function AccountCombobox({ value, onChange, accounts, placeholder }: {
  value: string; onChange: (v: string) => void; accounts: any[]; placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const t = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!t) return accounts.slice(0, 50);
    return accounts.filter((a: any) =>
      String(a.account_code).toLowerCase().startsWith(t) ||
      String(a.account_name).toLowerCase().includes(t)
    ).slice(0, 50);
  }, [t, accounts]);
  useEffect(() => { setHighlight(0); }, [t]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const pick = (a: any) => { onChange(`${a.account_code} ${a.account_name}`); setOpen(false); };
  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, matches.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
            else if (e.key === 'Enter' && open && matches[highlight]) { e.preventDefault(); pick(matches[highlight]); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          className="pl-8 pr-6 py-1.5 border rounded-lg text-xs bg-background w-64"
        />
        {value && (
          <button type="button" onClick={() => { onChange(''); setOpen(false); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
            aria-label={tr('Clear account filter', '清除科目篩選', '清除科目筛选')}>
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 w-80 max-h-64 overflow-y-auto border rounded-lg bg-card shadow-md text-xs">
          {matches.map((a: any, i: number) => (
            <div key={a.account_code}
              onMouseDown={e => { e.preventDefault(); pick(a); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-2.5 py-1.5 cursor-pointer flex items-baseline gap-2 ${i === highlight ? 'bg-muted' : ''}`}>
              <span className="font-mono font-semibold shrink-0">{a.account_code}</span>
              <span className="text-muted-foreground truncate">{a.account_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Bookkeeping({ initialTab, hideTabs }: { initialTab?: 'entries' | 'accounts' | 'trial' | 'pl' | 'bs' | 'ledger' | 'export'; hideTabs?: boolean }) {
  const { i18n } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff' || user?.role === 'viewer';
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'entries' | 'accounts' | 'trial' | 'pl' | 'bs' | 'ledger' | 'export'>(initialTab || 'entries');
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  // Force-refresh when switching to ledger tab — ensure fresh data after page navigation
  useEffect(() => {
    if (tab === 'ledger') queryClient.invalidateQueries({ queryKey: ['ledger'] });
    if (tab === 'entries') queryClient.invalidateQueries({ queryKey: ['entries'] });
    if (tab === 'pl') queryClient.invalidateQueries({ queryKey: ['income-statement'] });
  }, [tab]);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [ledgerAccount, setLedgerAccount] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { highlight: highlightId } = useHighlightTarget();
  const bookkeepingNavigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const entryRowRef = useRef<HTMLTableRowElement | null>(null);
  const plFocus = searchParams.get('pl_focus');
  const navigate = useNavigate();
  const [showPicker, setShowPicker] = useState(false);
  const [confirmNoDocs, setConfirmNoDocs] = useState(false);

  const [similarEntries, setSimilarEntries] = useState<any[] | null>(null);
  const [showAutoGenPanel, setShowAutoGenPanel] = useState(false);

  // Deep-link a bank transaction into its statement card on the Bank Statements page
  const handlePostClick = (bankStatementId: string, transactionId: string) => {
    navigate(`/bank-statements?statement=${encodeURIComponent(bankStatementId)}&highlight=${encodeURIComponent(transactionId)}`);
  };

  const { startDate, endDate } = useDateFilter();

  const openAccountDetails = (
    accountCode: string,
    returnTo: string,
    returnLabel: string,
    detailStartDate = startDate,
    detailEndDate = endDate,
  ) => {
    const params = new URLSearchParams({
      start_date: detailStartDate,
      end_date: detailEndDate,
      return: returnTo,
      returnLabel,
    });
    navigate(`/bookkeeping/accounts/${encodeURIComponent(accountCode)}/transactions?${params.toString()}`);
  };

  const accountRowEvents = (
    accountCode: string,
    returnTo: string,
    returnLabel: string,
    detailStartDate?: string,
    detailEndDate?: string,
  ) => ({
    onClick: () => openAccountDetails(accountCode, returnTo, returnLabel, detailStartDate, detailEndDate),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openAccountDetails(accountCode, returnTo, returnLabel, detailStartDate, detailEndDate);
      }
    },
  });

  const [entryForm, setEntryForm] = useState({
    entry_number: '', entry_date: new Date().toISOString().split('T')[0], description: '',
    lines: [{ account_code: '', account_name: '', description: '', debit: 0, credit: 0, project: '' }],
    files: [] as PickedFile[],
  });
  const [overrideVoucher, setOverrideVoucher] = useState(false);

  // GJE list filters (2026-10-14 overhaul) — source / status / account / free
  // text, all applied server-side so pagination never hides a match.
  const [entrySource, setEntrySource] = useState('');
  const [entryStatus, setEntryStatus] = useState('');
  const [entryAccountText, setEntryAccountText] = useState('');
  const [entrySearch, setEntrySearch] = useState('');
  const [entrySearchDebounced, setEntrySearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setEntrySearchDebounced(entrySearch.trim()), 300);
    return () => clearTimeout(t);
  }, [entrySearch]);

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api('/bookkeeping/accounts'),
    enabled: tab === 'accounts' || tab === 'ledger' || tab === 'entries',
  });
  // Only leaf (postable) accounts for journal-entry pickers — parents are groups
  const leafAccounts = useMemo(
    () => filterLeafAccounts(accounts?.data || []),
    [accounts]
  );

  // Resolve the account combobox's text to a code: an exact code, the picked
  // "code name" label, or a unique prefix/name hit. Anything else passes
  // through raw — the empty result list is the feedback.
  const entryAccount = useMemo(() => {
    const t = entryAccountText.trim();
    if (!t) return '';
    const ci = t.toLowerCase();
    if (leafAccounts.some((a: any) => String(a.account_code) === t)) return t;
    const byLabel = leafAccounts.find((a: any) => `${a.account_code} ${a.account_name}` === t);
    if (byLabel) return String(byLabel.account_code);
    const byCode = leafAccounts.filter((a: any) => String(a.account_code).toLowerCase().startsWith(ci));
    if (byCode.length === 1) return String(byCode[0].account_code);
    const byName = leafAccounts.filter((a: any) => String(a.account_name).toLowerCase().includes(ci));
    if (byName.length === 1) return String(byName[0].account_code);
    return t;
  }, [entryAccountText, leafAccounts]);
  const noAccountMatch = entryAccount !== '' &&
    !leafAccounts.some((a: any) => String(a.account_code) === entryAccount);

  const entryFilterQs = [
    entrySource && `source=${entrySource}`,
    entryStatus && `status=${entryStatus}`,
    entryAccount && `account=${encodeURIComponent(entryAccount)}`,
    entrySearchDebounced && `q=${encodeURIComponent(entrySearchDebounced)}`,
  ].filter(Boolean).join('&');

  const { data: entries } = useQuery({
    queryKey: ['entries', startDate, endDate, entrySource, entryStatus, entryAccount, entrySearchDebounced],
    queryFn: () => api(`/bookkeeping/entries?start_date=${startDate}&end_date=${endDate}&limit=${TABLE_FETCH_CAP}${entryFilterQs ? '&' + entryFilterQs : ''}`),
    enabled: tab === 'entries',
    staleTime: 0,
  });

  const entriesRows = entries?.data || [];
  const controls = useTableControls({ rows: entriesRows, columns: COLS, totalCount: entries?.meta?.total });

  // Fetch a specific entry when ?entry=<id> is in the URL (from review queue).
  // This bypasses the fiscal-year date filter so the entry is always visible.
  const entryParam = searchParams.get('entry');
  const { data: highlightedEntry } = useQuery({
    queryKey: ['entry', entryParam],
    queryFn: () => api(`/bookkeeping/entries/${entryParam}`),
    enabled: tab === 'entries' && !!entryParam,
  });

  // A deep-linked entry may be outside the selected fiscal year, so do not rely
  // on the table query. The popup query can fetch and display it directly.
  useEffect(() => {
    if (entryParam && tab === 'entries') setExpandedId(entryParam);
  }, [entryParam, tab]);

  const popupEntry = expandedId
    ? ((entries?.data || []).find((e: any) => e.id === expandedId)
      || (highlightedEntry?.id === expandedId ? highlightedEntry : null))
    : null;

  // Auto-expand from highlight navigation
  useEffect(() => {
    if (!highlightId || tab !== 'entries' || !entries?.data) return;
    const inTable = entries.data.find((e: any) => e.id === highlightId);
    setExpandedId(highlightId);

  }, [highlightId, entries?.data, tab]);

  // Reports count confirmed postings only (2026-10-28 client review: the
  // booked-vs-posted toggle was removed — statement-inferred drafts never
  // hit the financial statements).
  const { data: trialBalance } = useQuery({
    queryKey: ['trial-balance'],
    queryFn: () => api('/bookkeeping/trial-balance'),
    enabled: tab === 'trial',
  });

  const { data: incomeStatement } = useQuery({
    queryKey: ['income-statement', startDate, endDate],
    queryFn: () => api(`/bookkeeping/income-statement?start_date=${startDate}&end_date=${endDate}`),
    enabled: tab === 'pl' && !!startDate,
    staleTime: 0,
  });

  // Prior-year P&L for the statement's comparison column (vertical statement,
  // 2026-10-30): same endpoint with both dates shifted back one year.
  const shiftYear = (d: string, delta: number) => (d ? `${Number(d.slice(0, 4)) + delta}${d.slice(4)}` : d);
  const { data: priorIncomeStatement } = useQuery({
    queryKey: ['income-statement-prior', startDate, endDate],
    queryFn: () => api(`/bookkeeping/income-statement?start_date=${shiftYear(startDate || '', -1)}&end_date=${shiftYear(endDate || '', -1)}`),
    enabled: tab === 'pl' && !!startDate,
    staleTime: 0,
  });

  const { data: balanceSheet } = useQuery({
    queryKey: ['balance-sheet'],
    queryFn: () => api('/bookkeeping/balance-sheet'),
    enabled: tab === 'bs',
  });

  const { data: ledgerData, isLoading: ledgerLoading, isFetching } = useQuery({
    queryKey: ['ledger', ledgerAccount, startDate, endDate],
    queryFn: () => {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (ledgerAccount) params.set('account_code', ledgerAccount);
      const qs = params.toString();
      return api(`/bookkeeping/ledger${qs ? `?${qs}` : ''}`);
    },
    enabled: tab === 'ledger',
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: closedPeriods } = useQuery({
    queryKey: ['closed-periods'],
    queryFn: () => api('/bookkeeping/closed-periods'),
    enabled: showEntryForm,
  });
  const closedHit = (closedPeriods?.data || []).find(
    (p: any) => entryForm.entry_date >= p.period_start && entryForm.entry_date <= p.period_end,
  );

  const { data: nextNumber } = useQuery({
    queryKey: ['next-number', entryForm.entry_date],
    queryFn: () => api(`/bookkeeping/entries/next-number?date=${entryForm.entry_date}`),
    enabled: showEntryForm,
  });

  const linkQueries = useQueries({
    queries: entryForm.files.map(f => ({
      queryKey: ['file-links', f.id],
      queryFn: () => api(`/file-storage/${f.id}/linked-records`),
    })),
  });
  const fileWarnings = entryForm.files
    .map((f, i) => ({ file: f, links: (linkQueries[i]?.data?.links || []) as any[] }))
    .filter(w => w.links.length > 0);


  const createEntry = useMutation({
    mutationFn: (body: any) => api('/bookkeeping/entries', { method: 'POST', body }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      setShowEntryForm(false);
      setConfirmNoDocs(false);
      toast.success(tr(`Journal ${data.entry_number} saved for review`, `分錄 ${data.entry_number} 已儲存，等待審核`, `分录 ${data.entry_number} 已保存，等待审核`));
    },
    onError: (err: any) => {
      if (err?.body?.error_code === 'similar_entry_exists') {
        setSimilarEntries(err.body.similar_entries || []);
        return;
      }
      toast.error(err?.message || tr('Failed to post', '記錄失敗', '记录失败'));
    },
  });

  const confirmEntry = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/entries/${id}/status`, { method: 'PATCH', body: { status: 'posted' } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      toast.success(tr('Journal posted', '分錄已過賬', '分录已过账'));
    },
    onError: (err: any) => toast.error(err?.message || tr('Posting failed', '過賬失敗', '过账失败')),
  });

  const reverseEntry = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/entries/${id}/reverse`, { method: 'POST' }),
    onSuccess: (data: any) => {
      toast.success(tr(`Reversal posted: ${data.entry_number}`, `已記錄沖銷分錄 ${data.entry_number}`, `已记录冲销分录 ${data.entry_number}`));
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (err: any) => toast.error(err?.message || tr('Reversal failed', '沖銷失敗', '冲销失败')),
  });

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/bookkeeping/export?format=csv&start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { toast.info('Export failed'); return; }
      const csv = await res.text();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'bookkeeping-export.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { toast.info('Export error: ' + (e?.message || 'unknown')); }
  };

  // One workbook, one sheet per P&L section (Summary + Revenue + …), full
  // journal-line detail on the same confirmed-postings basis as the statement.
  const [plExporting, setPlExporting] = useState(false);
  const exportPnlExcel = async () => {
    if (plExporting) return;
    setPlExporting(true);
    try {
      const data = await api(`/bookkeeping/income-statement/detail?start_date=${startDate}&end_date=${endDate}`);
      exportPlXlsx(data);
      toast.success(tr('Income statement Excel downloaded', '損益表 Excel 已下載', '损益表 Excel 已下载'));
    } catch (e: any) {
      toast.error(e?.message || tr('Export failed', '導出失敗', '导出失败'));
    } finally {
      setPlExporting(false);
    }
  };

  function addLine() {
    setEntryForm({
      ...entryForm,
      lines: [...entryForm.lines, { account_code: '', account_name: '', description: '', debit: 0, credit: 0, project: '' }],
    });
  }

  function updateLine(idx: number, field: string, value: any) {
    const lines = [...entryForm.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    if (field === 'debit') lines[idx].credit = 0;
    if (field === 'credit') lines[idx].debit = 0;
    setEntryForm({ ...entryForm, lines });
  }

  // ── Shared helpers ──────────────────────────────────────────────────────
  const fmtMoney = (n: number) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  const totals = useMemo(() => {
    const debit = entryForm.lines.reduce((s, l) => s + (l.debit || 0), 0);
    const credit = entryForm.lines.reduce((s, l) => s + (l.credit || 0), 0);
    return { debit, credit, diff: debit - credit, balanced: Math.abs(debit - credit) <= 0.001 };
  }, [entryForm.lines]);
  const canSubmit = totals.balanced && entryForm.lines.length >= 2 && !createEntry.isPending;

  function handlePost() {
    if (!canSubmit || createEntry.isPending) return;
    if (entryForm.files.length === 0 && !confirmNoDocs) { setConfirmNoDocs(true); return; }
    const payload = {
      ...entryForm,
      entry_number: overrideVoucher ? entryForm.entry_number : undefined,
      file_ids: entryForm.files.map(f => f.id),
      duplicate_acknowledged: false,
    };
    createEntry.mutate(payload);
  }

  function suggestVoucherNumber(entryRows: any[] | undefined, _date: string, prefix = 'M'): string {
    const re = new RegExp(`^${prefix}\\d{7}$`);
    let max = 0;
    for (const r of entryRows || []) {
      const n = typeof r?.entry_number === 'string' && re.test(r.entry_number) ? parseInt(r.entry_number.slice(1), 10) : 0;
      if (n > max) max = n;
    }
    return `${prefix}${String(max + 1).padStart(7, '0')}`;
  }

  // Reset form with fresh voucher suggestion when modal opens
  useEffect(() => {
    if (!showEntryForm) return;
    setOverrideVoucher(false);
    const today = new Date().toISOString().split('T')[0];
    setEntryForm({
      entry_number: '',
      entry_date: today,
      description: '',
      lines: [{ account_code: '', account_name: '', description: '', debit: 0, credit: 0, project: '' }],
      files: [],
    });
  }, [showEntryForm]);

  // 'stale' is no longer a status — deleted entries carry deleted_at instead and
  // are filtered out server-side, so they never reach this table.
  function stageCell(s: string) {
    return (
      <LifecycleStepper type="voucher" variant="compact" stage={stageOf('voucher', { status: s })} />
    );
  }

  // Voucher-source badge: the letter prefix of the entry number says which
  // record spawned the voucher (B statement, I invoice, P payment, M manual…).
  function sourceBadge(entryNumber?: string | null) {
    const letter = /^[A-Z]/.exec(entryNumber || '')?.[0];
    const short: Record<string, [string, string, string]> = {
      B: ['Statement', '月結單', '月结单'], C: ['Card', '信用卡', '信用卡'], I: ['Invoice', '發票', '发票'],
      R: ['Receipt', '收據', '收据'], P: ['Payment', '付款', '付款'], M: ['Manual', '手動', '手动'],
      D: ['Depreciation', '折舊', '折旧'], Y: ['Year-end', '年結', '年结'], T: ['Tax', '稅項', '税项'],
    };
    const styles: Record<string, string> = {
      B: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      C: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      I: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      R: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
      P: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
      M: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      D: 'bg-slate-200 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
      Y: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      T: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    };
    if (!letter || !short[letter]) return <span className="text-xs text-muted-foreground">—</span>;
    const full = sourceLabel(entryNumber);
    return (
      <Tooltip content={full ? tr(full[0], full[1], full[2]) : letter}>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold cursor-help ${styles[letter]}`}>
          {tr(short[letter][0], short[letter][1], short[letter][2])}
        </span>
      </Tooltip>
    );
  }

  const tabs = [
    { id: 'entries', label: tr('GJE', 'GJE 日誌帳', 'GJE 日志帐') },
    { id: 'accounts', label: tr('Accounts', '科目 Accounts', '科目 Accounts') },
    { id: 'pl', label: tr('P&L', '損益 P&L', '损益 P&L') },
    { id: 'bs', label: tr('Balance Sheet', '資產負債 Balance Sheet', '资产负债表 Balance Sheet') },
    { id: 'trial', label: tr('Trial Balance', '試算 Trial Balance', '试算 Trial Balance') },
    { id: 'ledger', label: tr('Ledger', '分類帳 Ledger', '分类帐 Ledger') },
    { id: 'export', label: tr('Export', '導出 Export', '导出 Export') },
  ] as const;

  return (
    <div className="space-y-6">
      {hideTabs ? (
        <div>
          <h2 className="text-2xl font-bold">
            {tab === 'entries' ? tr('General Journal Entries (GJE)', '記帳日誌帳 (GJE)', '记账日志帐 (GJE)')
             : tab === 'pl' ? tr('Income Statement', '損益表', '损益表')
             : tab === 'trial' ? tr('Trial Balance', '試算表', '试算表')
             : tab === 'bs' ? tr('Balance Sheet', '資產負債表', '资产负债表')
             : tab === 'ledger' ? tr('General Ledger Report', '總帳報告', '总帐报告')
             : tab === 'export' ? tr('Export', '匯出', '导出')
             : tr('General Journal Entries (GJE)', '記帳日誌帳 (GJE)', '记账日志帐 (GJE)')}
          </h2>
          <p className="text-muted-foreground mt-1">
            {tab === 'entries' ? tr('The complete journal of the books — every voucher from statements, invoices, matches and adjustments.', '全部帳冊的完整日誌——來自月結單、發票、配對及調整的每一張憑證。', '全部账册的完整日志——来自月结单、发票、配对及调整的每一张凭证。')
             : tab === 'pl' ? tr('Revenue, expenses, and profit/loss for the selected period.', '所選期間的收入、支出及損益。', '所选期间的收入、支出及损益。')
             : tab === 'trial' ? tr('Debit and credit balances for all accounts at period end.', '期末所有科目的借方及貸方餘額。', '期末所有科目的借方及贷方余额。')
             : tab === 'bs' ? tr('Assets, liabilities, and equity as at the selected date.', '截至選定日期的資產、負債及權益。', '截至选定日期的资产、负债及权益。')
             : tab === 'ledger' ? tr('Detailed transaction report by account and period.', '按科目及期間的詳細交易報告。', '按科目及期间的详细交易报告。')
             : tab === 'export' ? tr('Export your bookkeeping data to CSV.', '將記帳數據匯出為 CSV。', '将记帐数据导出为 CSV。')
             : ''}
          </p>
        </div>
      ) : (
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">記帳 Bookkeeping</h2>
          <p className="text-muted-foreground mt-1">雙式記帳管理</p>
        </div>
        <button onClick={() => setShowEntryForm(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90">
          <Plus className="h-4 w-4" /> {tr('+ New GJE', '+ 新增日誌帳', '+ 新增日志帐')}
        </button>
      </div>
      )}

      {/* Tabs */}
      {!hideTabs && (
      <div className="flex gap-1 border-b">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>
      )}

      {/* Reports count confirmed postings only — the booked/posted toggle was
          removed per client review (2026-10-28); keep the basis caption. */}
      {(tab === 'pl' || tab === 'bs' || tab === 'trial') && (
        <span className="text-[11px] text-muted-foreground">
          {tr('Confirmed postings only — invoice and payment legs, manual entries.', '只計已確認過賬——發票及付款分錄、手動分錄。', '只计已确认过账——发票及付款分录、手动分录。')}
        </span>
      )}

      {/* Header controls bar for standalone GJE page */}
      {hideTabs && tab === 'entries' && (
        <div className="flex flex-wrap items-center gap-3 bg-card border rounded-xl px-4 py-3">
          <div className="flex-1" />
          {!isStaff && (
            <button onClick={() => setShowEntryForm(true)}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90">
              <Plus className="h-4 w-4" /> {tr('New General Journal Entry', '新增日誌帳', '新增日志帐')}
            </button>
          )}
          {!isStaff && (
            <button onClick={() => setShowAutoGenPanel(true)}
              className="flex items-center gap-2 border px-4 py-2 rounded-md text-sm font-medium hover:bg-muted">
              <RefreshCw className="h-4 w-4" />
              {tr('+ Auto-Generate Journal Entries', '+ 自動產生日誌分錄', '+ 自动产生日志分录')}
            </button>
          )}
          {!isStaff && (
            <button onClick={exportCSV}
              className="flex items-center gap-2 border px-4 py-2 rounded-md text-sm font-medium hover:bg-muted">
              <Download className="h-4 w-4" /> {tr('Export CSV', '匯出 CSV', '导出 CSV')}
            </button>
          )}
        </div>
      )}

      {/* Entries Tab */}
      {tab === 'entries' && (() => {
        // Server-side draft count (2026-10-14): the banner used to count the
        // loaded 50-row page and undercounted whenever drafts spilled past
        // page 1 (28 shown vs 41 in the fiscal year).
        const draftCount = entries?.meta?.drafts ?? (entries?.data || []).filter((e: any) => e.status === 'booked').length;
        const anyFilter = !!(entrySource || entryStatus || entryAccount || entrySearchDebounced);
        return (
        <div className="space-y-4">
          {/* Filter bar — source / status / account / search, server-side */}
          <div className="flex flex-wrap items-center gap-2 bg-card border rounded-xl px-3 py-2.5">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={entrySearch}
                onChange={e => setEntrySearch(e.target.value)}
                placeholder={tr('Search voucher no. / description', '搜尋憑證編號／備忘', '搜尋憑證編號／备记')}
                className="pl-8 pr-3 py-1.5 border rounded-lg text-xs bg-background w-56"
              />
            </div>
            <select value={entrySource} onChange={e => setEntrySource(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-xs bg-background"
              aria-label={tr('Filter by source', '按來源篩選', '按来源筛选')}>
              <option value="">{tr('All sources', '所有來源', '所有来源')}</option>
              <option value="B">{tr('Bank statement', '銀行月結單', '银行月结单')}</option>
              <option value="C">{tr('Card statement', '信用卡月結單', '信用卡月结单')}</option>
              <option value="I">{tr('Invoice', '發票', '发票')}</option>
              <option value="R">{tr('Expense receipt', '費用收據', '费用收据')}</option>
              <option value="P">{tr('Payment', '付款', '付款')}</option>
              <option value="M">{tr('Manual', '手動', '手动')}</option>
              <option value="D">{tr('Depreciation', '折舊', '折旧')}</option>
              <option value="Y">{tr('Year-end', '年結', '年结')}</option>
              <option value="T">{tr('Tax provision', '稅項撥備', '税项拨备')}</option>
            </select>
            <select value={entryStatus} onChange={e => setEntryStatus(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-xs bg-background"
              aria-label={tr('Filter by status', '按狀態篩選', '按状态筛选')}>
              <option value="">{tr('All statuses', '所有狀態', '所有状态')}</option>
              <option value="booked">{tr('Booked', '已入賬', '已入账')}</option>
              <option value="posted">{tr('Posted', '已過賬', '已过账')}</option>
              <option value="reconciled">{tr('Reconciled', '已對帳', '已对账')}</option>
            </select>
            <AccountCombobox value={entryAccountText} onChange={setEntryAccountText} accounts={leafAccounts}
              placeholder={tr('Account — type code or name', '科目——輸入代碼或名稱', '科目——输入代码或名称')} />
            {noAccountMatch && (
              <span className="text-[10px] text-amber-700 dark:text-amber-300">{tr('no such account', '無此科目', '无此科目')}</span>
            )}
            {anyFilter && (
              <button onClick={() => { setEntrySource(''); setEntryStatus(''); setEntryAccountText(''); setEntrySearch(''); }}
                className="inline-flex items-center gap-1 px-2 py-1 border rounded-lg text-xs hover:bg-muted">
                <X className="h-3 w-3" /> {tr('Clear', '清除', '清除')}
              </button>
            )}
            <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
              {entries?.meta?.total ?? (entries?.data || []).length} {tr('entries', '筆', '笔')}
              {' · '}{draftCount} {tr('unposted', '未過賬', '未过账')}
            </span>
          </div>

          {draftCount > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-900 dark:text-amber-100 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    {draftCount} {tr('unposted journal entries', '筆未過賬分錄', '笔未过账分录')}
                  </p>
                  <p className="text-xs text-amber-900 dark:text-amber-100">
                    {tr(
                      'Counts in the All-entries report view; excluded from Confirmed postings until confirmed.',
                      '計入全部分錄報表視圖；未確認前不計入已確認過賬。',
                      '计入全部分录报表视图；未确认前不计入已确认过账。'
                    )}
                  </p>
                </div>
              </div>
              <a href="/review-queue" className="px-3 py-1.5 text-xs bg-amber-900 text-white rounded hover:bg-amber-800 shrink-0">
                Review →
              </a>
            </div>
          )}
        <div className="bg-card border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-8 p-3"></th>
                <Th controls={controls} colKey="entry_number" className="text-left">{tr('Voucher No.', '總帳 #', '总帐 #')}</Th>
                <th className="text-left p-3">{tr('Source', '來源', '来源')}</th>
                <Th controls={controls} colKey="entry_date" className="text-left">{tr('Date', '日期', '日期')}</Th>
                <Th controls={controls} colKey="description" className="text-left">{tr('Description', '備忘', '备记')}</Th>
                <Th controls={controls} colKey="total_debit" align="right">{tr('Debit ($Dr$)', '借方 ($Dr$)', '借方 ($Dr$)')}</Th>
                <Th controls={controls} colKey="total_credit" align="right">{tr('Credit ($Cr$)', '貸方 ($Cr$)', '贷方 ($Cr$)')}</Th>
                <Th controls={controls} colKey="status" className="text-left">{tr('Status', '狀態', '状态')}</Th>
                <th className="text-left p-3">{tr('Created by', '建立者', '建立者')}</th>
                <th className="text-center p-3 w-[100px]">{tr('Actions', '操作', '操作')}</th>
              </tr>
            </thead>
            <tbody>
              {controls.visibleRows.map((e: any) => (
                <React.Fragment key={e.id}>
                <tr id={`entry-row-${e.id}`} onClick={() => { setExpandedId(e.id); }} className={`border-b hover:bg-muted/30 cursor-pointer ${e.status === 'booked' ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                  <td className="p-3">
                    <span className="p-0.5 inline-flex">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </span>
                  </td>
                  <td className="p-3 font-medium font-mono text-xs">
                    {e.entry_number}
                    {e.reversed && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-950/40">
                        ↩ {tr('reversed', '已沖銷', '已冲销')}
                      </span>
                    )}
                  </td>
                  <td className="p-3">{sourceBadge(e.entry_number)}</td>
                  <td className="p-3">{e.entry_date}</td>
                  <td className="p-3 max-w-[200px] truncate" title={e.description}>{e.description}</td>
                  <td className="p-3 text-right font-mono">{e.total_debit > 0 ? '$' + fmtMoney(e.total_debit) : ''}</td>
                  <td className="p-3 text-right font-mono">{e.total_credit > 0 ? '$' + fmtMoney(e.total_credit) : ''}</td>
                  <td className="p-3">{stageCell(e.status)}</td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {e.created_by?.name || e.created_by?.email || '—'}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={(ev) => { ev.stopPropagation(); setExpandedId(e.id); }} className="text-primary text-xs hover:underline" title={tr('View details & audit trail', '查看詳情及審計軌跡', '查看详情及审计轨迹')}>
                        {tr('View', '查看', '查看')}
                      </button>
                      {!isStaff && (
                        <>
                          {e.status === 'booked' && (
                            <button onClick={(ev) => { ev.stopPropagation(); if (window.confirm(tr('Confirm and post this journal entry?', '確認並過賬此分錄？', '确认并过账此分录？'))) confirmEntry.mutate(e.id); }}
                              className="px-2 py-1 rounded bg-primary text-primary-foreground text-xs hover:opacity-90"
                              disabled={confirmEntry.isPending}
                              title={tr('Confirm and post', '確認並過賬', '确认并过账')}>
                              {tr('Confirm & post', '確認並過賬', '确认并过账')}
                            </button>
                          )}
                          <button onClick={(ev) => { ev.stopPropagation(); if (window.confirm(tr(`Post a reversal of ${e.entry_number}?`, `記錄 ${e.entry_number} 的沖銷分錄？`, `记录 ${e.entry_number} 的冲销分录？`))) reverseEntry.mutate(e.id); }}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title={tr('Reverse entry', '沖銷分錄', '冲销分录')}>
                            <RotateCcw className="h-4 w-4" />
                          </button>
                          <button onClick={(ev) => { ev.stopPropagation(); if (!confirm(tr('Confirm delete? This action cannot be undone.', '確定要刪除此分錄嗎？此操作不可撤銷。', '确定要删除此分录吗？此操作不可撤销。'))) return; api(`/bookkeeping/entries/${e.id}`, { method: 'DELETE' }).then(() => { queryClient.invalidateQueries({ queryKey: ['entries'] }); }).catch(err => toast.info(tr('Delete failed: ', '刪除失敗：', '删除失败：') + (err.message || ''))); }} className="text-destructive text-xs hover:underline">{tr('Delete', '刪除', '删除')}</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              ))}
              {(!entries?.data || entries.data.length === 0) && (
                <tr><td colSpan={10} className="text-center p-6 text-muted-foreground">{tr('No journal entries recorded.', '未有日誌帳記錄', '未有日志帐记录')}</td></tr>
              )}
              {entriesRows.length > 0 && controls.filteredCount === 0 && (
                <tr><td colSpan={10} className="text-center p-6 text-muted-foreground">{tr('No entries match the filters.', '沒有符合篩選的分錄', '没有符合筛选的分录')}</td></tr>
              )}
            </tbody>
          </table>
          <TablePager controls={controls} />
        </div>

        {popupEntry && (
          <JournalEntryPopup entry={popupEntry} onClose={() => setExpandedId(null)} />
        )}
        </div>
        );
        })()}

      {/* Accounts Tab */}
      {tab === 'accounts' && <AccountsTab accounts={accounts?.data || []} />}

      {/* Trial Balance Tab */}
      {tab === 'trial' && (
        <div className="bg-card border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3">科目</th>
                <th className="text-right p-3">承前結餘 Balance b/f</th>
                <th className="text-right p-3">借方 Debit ($Dr$)</th>
                <th className="text-right p-3">貸方 Credit ($Cr$)</th>
                <th className="text-right p-3">期末 Ending</th>
                <th className="w-10" aria-label={tr('View details', '查看詳情', '查看详情')} />
              </tr>
            </thead>
            <tbody>
              {(trialBalance?.data || []).map((row: any) => (
                <tr
                  key={row.account_code}
                  tabIndex={0}
                  role="button"
                  aria-label={`${row.account_code} ${row.account_name}. ${tr('View account details', '查看科目詳情', '查看科目详情')}`}
                  {...accountRowEvents(row.account_code, '/trial-balance', tr('Trial Balance', '試算表', '试算表'), '2000-01-01', trialBalance?.as_of || endDate)}
                  className="cursor-pointer border-b transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <td className="p-3">
                    <div className="font-medium text-foreground">{row.account_code} – {row.account_name}</div>
                    <div className="mt-0.5 text-xs text-primary">{tr('View details', '查看詳情', '查看详情')}</div>
                  </td>
                  <td className="p-3 text-right font-mono">{(row.opening_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right font-mono">{(row.total_debit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right font-mono">{(row.total_credit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right font-mono font-medium">{(row.ending_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right text-muted-foreground"><ChevronRight className="ml-auto h-4 w-4" /></td>
                </tr>
              ))}
              {(trialBalance?.data || []).length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{tr('No trial balance data.', '沒有試算表資料', '没有试算表资料')}</td></tr>
              )}
            </tbody>
            {(trialBalance?.data || []).length > 0 && (
              <tfoot>
                {/* Proof row — Dr and Cr column totals must agree (mirrors the
                    Excel export). Opening/Ending stay blank: their signs differ
                    by account class, so a column sum would be meaningless. */}
                <tr className="border-t-2 border-foreground/20 bg-muted/50 font-semibold">
                  <td className="p-3">{tr('Total', '總計', '总计')}</td>
                  <td className="p-3" />
                  <td className="p-3 text-right font-mono">{(trialBalance?.data || []).reduce((s: number, r: any) => s + (r.total_debit || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right font-mono">{(trialBalance?.data || []).reduce((s: number, r: any) => s + (r.total_credit || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3" />
                  <td className="p-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Ledger Tab */}
      {tab === 'ledger' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <DropdownSelect
              key={`ledger-acct-${accounts?.data?.length || 0}`}
              value={ledgerAccount}
              options={[
                { value: '', label: tr('All Accounts', '所有科目', '所有科目') },
                ...(accounts?.data || []).map((a: any) => ({ value: a.account_code, label: `${a.account_code} – ${a.account_name}` })),
              ]}
              onChange={setLedgerAccount}
              className="min-w-[180px]"
            />
            <span className="text-xs text-muted-foreground">
              {tr('Source:', '資料來源：', '资料来源：')}
              {ledgerData?.source === 'journal' ? tr('Journal Entries', '分錄', '分录') : tr('Bank Transactions', '銀行交易', '银行交易')}
            </span>
          </div>

          {ledgerLoading ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : (ledgerData?.accounts || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">未有分類帳資料</p>
          ) : (
            (ledgerData?.accounts || []).map((acct: any) => (
              <div key={acct.account_code} className="bg-card border rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/50 border-b flex items-center justify-between">
                  <span className="font-medium text-sm">{acct.account_code} – {acct.account_name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{acct.account_type}</span>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="text-left py-2 px-4 font-medium">日期</th>
                      <th className="text-left py-2 px-3 font-medium">描述</th>
                      <th className="text-right py-2 px-3 font-medium">借方 Debit ($Dr$)</th>
                      <th className="text-right py-2 px-3 font-medium">貸方 Credit ($Cr$)</th>
                      <th className="text-right py-2 px-3 font-medium">餘額 Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acct.entries.map((e: any, i: number) => (
                      <tr key={i} className="border-b border-muted/30 hover:bg-muted/20">
                        <td className="py-1.5 px-4 whitespace-nowrap text-muted-foreground">{e.date}</td>
                        <td className="py-1.5 px-3 max-w-[300px] truncate">{e.description}</td>
                        <td className="py-1.5 px-3 text-right font-mono">{e.debit > 0 ? e.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</td>
                        <td className="py-1.5 px-3 text-right font-mono">{e.credit > 0 ? e.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</td>
                        <td className={`py-1.5 px-3 text-right font-mono font-medium ${e.balance >= 0 ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>
                          {e.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30 text-xs font-medium">
                      <td className="py-2 px-4" colSpan={2}>合計</td>
                      <td className="py-2 px-3 text-right font-mono">{acct.total_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="py-2 px-3 text-right font-mono">{acct.total_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))
          )}
        </div>
      )}

      {/* P&L Tab */}
      {tab === 'pl' && incomeStatement && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {!isStaff && (
              <button
                onClick={exportPnlExcel}
                disabled={plExporting}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm hover:opacity-90 disabled:opacity-60"
              >
                <Download className="h-4 w-4" />
                {plExporting
                  ? tr('Preparing…', '準備中…', '准备中…')
                  : tr('Export Excel (breakdown)', '導出 Excel（明細）', '导出 Excel（明细）')}
              </button>
            )}
          </div>
          <PnlFormulaBanner
            prior={priorIncomeStatement || undefined}
            data={{
              revenue: incomeStatement.revenue || 0,
              cost: incomeStatement.cost || 0,
              gross_profit: incomeStatement.gross_profit || 0,
              expenses: incomeStatement.operating_expenses ?? incomeStatement.expenses ?? 0,
              operating_profit: incomeStatement.operating_profit || 0,
              other_income: incomeStatement.other_income || 0,
              other_expenses: incomeStatement.other_expenses || 0,
              net_income: incomeStatement.net_income || 0,
              revenue_accounts: incomeStatement.revenue_accounts || [],
              cost_accounts: incomeStatement.cost_accounts || [],
              expense_accounts: incomeStatement.expense_accounts || [],
              other_income_accounts: incomeStatement.other_income_accounts || [],
              other_expense_accounts: incomeStatement.other_expense_accounts || [],
              source: incomeStatement.source,
              include_drafts: false,
              period: incomeStatement.period,
            }}
            focus={plFocus}
            onOpenBankTransaction={handlePostClick}
          />
        </div>
      )}

          {/* Balance Sheet Tab */}
      {tab === 'bs' && balanceSheet && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            {tr('As of', '截至', '截至')}: {balanceSheet.as_of} | {tr('Source', '來源', '来源')}: {balanceSheet.source === 'journal' ? tr('Journal', '分錄', '分录') : tr('Bank Estimate', '銀行交易估算', '银行交易估算')}
          </p>

          {/* Assets */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-blue-50 text-blue-900 font-bold border-b">
              資產 Assets
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(balanceSheet.assets || []).map((a: any) => (
                  <tr
                    key={a.code}
                    tabIndex={0}
                    role="button"
                    aria-label={`${a.code} ${a.name}. ${tr('View account details', '查看科目詳情', '查看科目详情')}`}
                    {...accountRowEvents(a.code, '/balance-sheet', tr('Balance Sheet', '資產負債表', '资产负债表'), '2000-01-01', balanceSheet.as_of)}
                    className="cursor-pointer border-b border-muted/30 transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <td className="py-2 px-4">{a.code} – {a.name}</td>
                    <td className="py-2 px-4 text-right font-mono">{a.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="w-8 pr-3 text-muted-foreground"><ChevronRight className="ml-auto h-4 w-4" /></td>
                  </tr>
                ))}
                <tr className="bg-muted/30 font-bold">
                  <td className="py-2.5 px-4">總資產 Total Assets</td>
                  <td className="py-2.5 px-4 text-right font-mono">{balanceSheet.total_assets?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Liabilities */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-rose-50 text-rose-900 font-bold border-b">
              負債 Liabilities
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(balanceSheet.liabilities || []).length === 0 ? (
                  <tr><td colSpan={3} className="py-4 px-4 text-center text-muted-foreground">無負債項目</td></tr>
                ) : (
                  (balanceSheet.liabilities || []).map((l: any) => (
                    <tr
                      key={l.code}
                      tabIndex={0}
                      role="button"
                      aria-label={`${l.code} ${l.name}. ${tr('View account details', '查看科目詳情', '查看科目详情')}`}
                      {...accountRowEvents(l.code, '/balance-sheet', tr('Balance Sheet', '資產負債表', '资产负债表'), '2000-01-01', balanceSheet.as_of)}
                      className="cursor-pointer border-b border-muted/30 transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                    >
                      <td className="py-2 px-4">{l.code} – {l.name}</td>
                      <td className="py-2 px-4 text-right font-mono">{l.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="w-8 pr-3 text-muted-foreground"><ChevronRight className="ml-auto h-4 w-4" /></td>
                    </tr>
                  ))
                )}
                <tr className="bg-muted/30 font-bold">
                  <td className="py-2.5 px-4">總負債 Total Liabilities</td>
                  <td className="py-2.5 px-4 text-right font-mono">{balanceSheet.total_liabilities?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Equity */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-emerald-50 text-emerald-900 font-bold border-b">
              股東權益 Equity
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(balanceSheet.equity || []).map((e: any) => (
                  <tr
                    key={e.code}
                    tabIndex={0}
                    role="button"
                    aria-label={`${e.code} ${e.name}. ${tr('View account details', '查看科目詳情', '查看科目详情')}`}
                    {...accountRowEvents(e.code, '/balance-sheet', tr('Balance Sheet', '資產負債表', '资产负债表'), '2000-01-01', balanceSheet.as_of)}
                    className="cursor-pointer border-b border-muted/30 transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <td className="py-2 px-4">{e.code} – {e.name}</td>
                    <td className="py-2 px-4 text-right font-mono">{e.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="w-8 pr-3 text-muted-foreground"><ChevronRight className="ml-auto h-4 w-4" /></td>
                  </tr>
                ))}
                <tr className="bg-muted/30 font-bold">
                  <td className="py-2.5 px-4">總權益 Total Equity</td>
                  <td className="py-2.5 px-4 text-right font-mono">{balanceSheet.total_equity?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Accounting Equation Check */}
          <div className={`p-3 rounded-lg text-sm font-medium text-center ${balanceSheet.check ? 'bg-green-50 dark:bg-green-950/30 text-green-700' : 'bg-red-50 dark:bg-red-950/30 text-red-700'}`}>
            {balanceSheet.check
              ? `✓ 會計等式平衡：Assets (${balanceSheet.total_assets?.toLocaleString()}) = Liabilities (${balanceSheet.total_liabilities?.toLocaleString()}) + Equity (${balanceSheet.total_equity?.toLocaleString()})`
              : `⚠ 會計等式不平衡！差異：${Math.abs((balanceSheet.total_assets || 0) - ((balanceSheet.total_liabilities || 0) + (balanceSheet.total_equity || 0))).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          </div>
        </div>
      )}

      {/* Export Tab */}
      {tab === 'export' && (
        <div className="bg-card border rounded-xl p-6 space-y-4">
          <h3 className="font-semibold">{tr('Export for Auditor', '導出給審計師 Export for Auditor', '导出給审计师 Export for Auditor')}</h3>
          <p className="text-sm text-muted-foreground">{tr('Select a date range and export CSV file', '選擇日期範圍後導出 CSV 檔案', '选择日期范围後导出 CSV 档案')}</p>
          <div className="flex gap-3 items-center">
            <input type="date" value={startDate} readOnly
              className="px-3 py-2 border rounded-md bg-background text-sm" />
            <span className="text-muted-foreground">至</span>
            <input type="date" value={endDate} readOnly
              className="px-3 py-2 border rounded-md bg-background text-sm" />
            {!isStaff && (
            <button onClick={exportCSV}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm hover:opacity-90">
              <Download className="h-4 w-4" /> {tr('Export CSV', '導出 CSV', '导出 CSV')}
            </button>
            )}
          </div>
        </div>
      )}

      {showAutoGenPanel && (
        <AutoGenerateSuggestionPanel onDone={() => setShowAutoGenPanel(false)} />
      )}

      {/* Entry Form Modal — GJE 輸入日誌帳 */}
      {showEntryForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto" onClick={() => setShowEntryForm(false)}>
          <div className="bg-card border rounded-xl p-6 w-full max-w-4xl mx-4 my-8 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg">{tr('Create / Edit Journal Entry', '輸入日誌帳', '输入日志帐')}</h3>
            <form className="space-y-3">
              {/* Header fields: Voucher No., Date, Narration */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{tr('Voucher No.', '總帳 #', '总帐 #')}</label>
                  <div className="flex items-center gap-2">
                    {overrideVoucher ? (
                      <input value={entryForm.entry_number}
                        onChange={(e) => setEntryForm({ ...entryForm, entry_number: e.target.value })}
                        placeholder="M0000001" className="flex-1 px-3 py-2 border rounded-md bg-background text-sm font-mono" />
                    ) : (
                      <div className="flex-1 px-3 py-2 border rounded-md bg-muted text-sm font-mono text-muted-foreground">
                        {nextNumber?.entry_number || suggestVoucherNumber(entries?.data, entryForm.entry_date)}
                      </div>
                    )}
                    <button type="button" 
                      onClick={() => {
                        if (!overrideVoucher) {
                          setOverrideVoucher(true);
                          setEntryForm({ ...entryForm, entry_number: nextNumber?.entry_number || suggestVoucherNumber(entries?.data, entryForm.entry_date) });
                        } else {
                          setOverrideVoucher(false);
                          setEntryForm({ ...entryForm, entry_number: '' });
                        }
                      }}
                      className="px-2.5 py-2 border rounded-md text-xs hover:bg-muted flex items-center gap-1 shrink-0">
                      {overrideVoucher ? (
                        <><RefreshCw className="h-3 w-3" /> {tr('Auto', '🔄', '🔄')}</>
                      ) : (
                        <><Pencil className="h-3 w-3" /> {tr('Override', '覆寫', '覆写')}</>
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{tr('Date', '日期', '日期')}</label>
                  <input type="date" required value={entryForm.entry_date} onChange={(e) => setEntryForm({ ...entryForm, entry_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{tr('Narration', '備忘', '备记')}</label>
                  <input required value={entryForm.description} onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                    placeholder={tr('Narration', '備忘', '备记')} className="w-full px-3 py-2 border rounded-md bg-background text-sm" />
                </div>
              </div>

              {/* Lines table */}
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b text-xs text-muted-foreground">
                      <th className="py-2 px-2 w-8 font-medium">#</th>
                      <th className="py-2 px-2 text-left font-medium">{tr('Account #', '科目編號', '科目编号')}</th>
                      <th className="py-2 px-2 text-left font-medium">{tr('Account Name', '科目名稱', '科目名称')}</th>
                      <th className="py-2 px-2 text-right font-medium w-[120px]">{tr('Debit ($Dr$)', '借方 ($Dr$)', '借方 ($Dr$)')}</th>
                      <th className="py-2 px-2 text-right font-medium w-[120px]">{tr('Credit ($Cr$)', '貸方 ($Cr$)', '贷方 ($Cr$)')}</th>
                      <th className="py-2 px-2 text-left font-medium">{tr('Project/Item', '項目', '项目')}</th>
                      <th className="py-2 px-2 text-left font-medium">{tr('Line Memo', '記帳備忘', '记帐备记')}</th>
                      <th className="py-2 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryForm.lines.map((line, idx) => {
                      const matchedAccount = (accounts?.data || []).find((a: any) => a.account_code === line.account_code);
                      const typeBadge = matchedAccount ? ({
                        asset: 'bg-blue-100 text-blue-700', liability: 'bg-orange-100 text-orange-700',
                        equity: 'bg-green-100 text-green-700', revenue: 'bg-emerald-100 text-emerald-700', cost: 'bg-orange-100 text-orange-700', expense: 'bg-red-100 text-red-700',
                        other_income: 'bg-emerald-100 text-emerald-700', other_expense: 'bg-rose-100 text-rose-700',
                      } as Record<string, string>)[matchedAccount.account_type] || '' : '';
                      const side = matchedAccount ? normalSide(matchedAccount.account_type) : '';
                      return (
                        <tr key={idx} className="border-b border-muted/30 hover:bg-muted/20">
                          <td className="py-1.5 px-2 text-muted-foreground text-xs text-center">{idx + 1}</td>
                          <td className="py-1.5 px-2">
                            <input required value={line.account_code}
                              onChange={(e) => {
                                const code = e.target.value;
                                updateLine(idx, 'account_code', code);
                                const match = (accounts?.data || []).find((a: any) => a.account_code === code);
                                if (match) updateLine(idx, 'account_name', match.account_name);
                              }}
                              placeholder={tr('Account #', '科目編號', '科目编号')}
                              list="account-list"
                              className="w-[90px] px-2 py-1 border rounded text-xs font-mono" />
                          </td>
                          <td className="py-1.5 px-2">
                            <div className="flex flex-col gap-0.5">
                              <select value={line.account_name}
                                onChange={(e) => {
                                  const name = e.target.value;
                                  updateLine(idx, 'account_name', name);
                                  const match = (accounts?.data || []).find((a: any) => a.account_name === name);
                                  if (match) updateLine(idx, 'account_code', match.account_code);
                                }}
                                className="w-full px-2 py-1 border rounded text-xs bg-background min-w-[130px]">
                                <option value="">{tr('Select...', '選擇科目...', '选择科目...')}</option>
                                {leafAccounts.map((a: any) => (
                                  <option key={a.id} value={a.account_name}>{a.account_code} – {a.account_name}</option>
                                ))}
                              </select>
                              {matchedAccount && (
                                <div className="flex items-center gap-1">
                                  <span className={`text-[10px] px-1 py-0 rounded ${typeBadge}`}>{matchedAccount.account_type}</span>
                                  <span className="text-[10px] text-muted-foreground">({side})</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-1.5 px-2">
                            <input type="number" step="0.01" min="0"
                              value={line.debit || ''} onChange={(e) => updateLine(idx, 'debit', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border rounded text-xs text-right font-mono" placeholder="0.00" />
                          </td>
                          <td className="py-1.5 px-2">
                            <input type="number" step="0.01" min="0"
                              value={line.credit || ''} onChange={(e) => updateLine(idx, 'credit', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border rounded text-xs text-right font-mono" placeholder="0.00" />
                          </td>
                          <td className="py-1.5 px-2">
                            <input value={line.project || ''} onChange={(e) => updateLine(idx, 'project', e.target.value)}
                              placeholder={tr('Optional', '可選', '可选')} className="w-full px-2 py-1 border rounded text-xs" />
                          </td>
                          <td className="py-1.5 px-2">
                            <input value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)}
                              placeholder={tr('Line memo', '記帳備忘', '记帐备记')} className="w-full px-2 py-1 border rounded text-xs" />
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <button type="button" onClick={() => {
                              const lines = entryForm.lines.filter((_, i) => i !== idx);
                              setEntryForm({ ...entryForm, lines: lines.length ? lines : [{ account_code: '', account_name: '', description: '', debit: 0, credit: 0, project: '' }] });
                            }} className="text-destructive text-xs hover:bg-destructive/10 rounded p-1">✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-3 py-2 border-t flex justify-between items-center">
                  <button type="button" onClick={addLine} className="text-xs text-primary hover:underline">{tr('+ Add Line', '+ 新增行', '+ 新增行')}</button>
                  <span className="text-xs text-muted-foreground">{entryForm.lines.length} {tr('line(s)', '行', '行')}</span>
                </div>
              </div>

              {/* Closed period hint */}
              {closedHit && (
                <p className="text-xs text-red-900 dark:text-red-200">
                  {tr('This date falls in a closed period — posting is locked.', '此日期屬於已關帳期間 — 記帳已鎖定。', '此日期属于已关账期间 — 记账已锁定。')}
                </p>
              )}

              {/* Attachments section */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-muted-foreground">{tr('Supporting documents', '證明文件', '证明文件')}</span>
                  {entryForm.files.map(f => (
                    <span key={f.id} className="inline-flex items-center gap-1 text-xs border rounded-md px-2 py-1">
                      <Paperclip className="h-3 w-3" />{f.filename}
                      <button onClick={() => setEntryForm({ ...entryForm, files: entryForm.files.filter(x => x.id !== f.id) })}
                        className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                  <button onClick={() => setShowPicker(true)} disabled={entryForm.files.length >= 10}
                    className="text-xs text-primary hover:underline disabled:opacity-50">
                    {tr('+ attach documents', '+ 附加文件', '+ 附加文件')}
                  </button>
                </div>
                {fileWarnings.map(w => (
                  <div key={w.file.id} className="flex items-start gap-2 text-xs bg-amber-100 dark:bg-amber-950/40 text-amber-800 rounded-md px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      <b>{w.file.filename}</b>{' '}
                      {tr('is already linked to:', '已連結至：', '已链接至：')}{' '}
                      {w.links.map(l => l.label).join('; ')}
                    </span>
                  </div>
                ))}
                {confirmNoDocs && entryForm.files.length === 0 && (
                  <div className="flex items-start gap-2 text-xs bg-amber-100 dark:bg-amber-950/40 text-amber-800 rounded-md px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{tr(
                      'No supporting document attached. HK companies should keep records supporting each entry — post anyway?',
                      '未附加證明文件。香港公司應保留每筆記帳的證明文件 — 仍要記錄？',
                      '未附加证明文件。香港公司应保留每笔记账的证明文件 — 仍要记录？',
                    )}</span>
                  </div>
                )}
              </div>

              {/* Balance check footer */}
              <div className="border rounded-md p-4 space-y-2 bg-muted/20">
                <div className="flex justify-end gap-8 text-sm">
                  <div className="text-right">
                    <span className="text-muted-foreground">{tr('Total Debit', '總借項', '总借项')}: </span>
                    <span className="font-mono font-medium">HK$ {fmtMoney(totals.debit)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">{tr('Total Credit', '總貸項', '总贷项')}: </span>
                    <span className="font-mono font-medium">HK$ {fmtMoney(totals.credit)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">{tr('Difference', '差額', '差额')}: </span>
                    <span className={`font-mono font-medium ${totals.balanced ? 'text-green-900 dark:text-green-200' : 'text-red-900 dark:text-red-200'}`}>HK$ {fmtMoney(Math.abs(totals.diff))}</span>
                  </div>
                </div>
                <div aria-live="polite" className={`flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium ${
                  totals.balanced ? 'bg-green-100 dark:bg-green-950/40 text-green-700' : 'bg-red-100 dark:bg-red-950/40 text-red-700'
                }`}>
                  {totals.balanced ? (
                    <>{tr('✓ Balanced', '✓ 已平衡', '✓ 已平衡')}</>
                  ) : (
                    <>{tr('⚠ Unbalanced — Debits must equal credits', '⚠ 不平衡 — 借貸必須相等', '⚠ 不平衡 — 借贷必须相等')}</>
                  )}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => { setShowEntryForm(false); setConfirmNoDocs(false); }} className="px-4 py-2 border rounded-md text-sm">
                  {tr('Cancel', '取消', '取消')}
                </button>
                <button type="button" onClick={handlePost} disabled={!canSubmit || createEntry.isPending}
                  title={!totals.balanced ? tr('Debits must equal credits', '借貸不平衡', '借贷不平衡') : entryForm.lines.length < 2 ? tr('At least 2 lines required', '至少需要兩行', '至少需要两行') : ''}
                  className={`px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                    confirmNoDocs && entryForm.files.length === 0
                      ? 'bg-amber-800 text-white hover:bg-amber-900'
                      : 'bg-primary text-primary-foreground'
                  }`}>
                  {confirmNoDocs && entryForm.files.length === 0
                    ? tr('Post without documents', '不附文件記錄', '不附文件记录')
                    : tr('Post Entry', '記錄', '记录')}
                </button>
              </div>
            </form>
            <datalist id="account-list">
              {leafAccounts.map((a: any) => (
                <option key={a.id} value={a.account_code}>{a.account_code} – {a.account_name}</option>
              ))}
            </datalist>
          </div>
        </div>
      )}

      {showPicker && (
        <DocumentPickerModal
          alreadyPicked={entryForm.files.map(f => f.id)}
          onPick={(picked) => setEntryForm({ ...entryForm, files: [...entryForm.files, ...picked.filter(p => !entryForm.files.some(x => x.id === p.id))].slice(0, 10) })}
          onClose={() => setShowPicker(false)}
        />
      )}

      {similarEntries !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSimilarEntries(null)}>
          <div className="bg-card border rounded-xl p-6 w-full max-w-lg mx-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-900 dark:text-amber-100" />
              {tr('Similar entry already exists', '已存在類似分錄', '已存在类似分录')}
            </h3>
            <ul className="text-sm space-y-1">
              {similarEntries.map((s: any) => (
                <li key={s.id} className="font-mono text-xs border rounded px-2 py-1">
                  {s.entry_number} · {s.description} · HK$ {fmtMoney(s.total_debit)}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              {tr('Same date, amount and account as an existing live entry. Book again anyway?', '與現有分錄的日期、金額和科目相同。仍要再次記錄？', '与现有分录的日期、金额和科目相同。仍要再次记录？')}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setSimilarEntries(null)} className="px-4 py-2 border rounded-md text-sm">{tr('Cancel', '取消', '取消')}</button>
              <button onClick={() => {
                setSimilarEntries(null);
                const payload = {
                  ...entryForm,
                  entry_number: overrideVoucher ? entryForm.entry_number : undefined,
                  file_ids: entryForm.files.map(f => f.id),
                  duplicate_acknowledged: true,
                };
                createEntry.mutate(payload);
              }}
                className="px-4 py-2 bg-amber-800 text-white rounded-md text-sm hover:bg-amber-900">
                {tr('Post anyway', '仍要記錄', '仍要记录')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════ Accounts Tab with B/F balance & fiscal period ═══════

function AccountsTab({ accounts }: { accounts: any[] }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [bfEdits, setBfEdits] = useState<Record<string, string>>({});
  const [fiscalStart, setFiscalStart] = useState('');
  const [fiscalEnd, setFiscalEnd] = useState('');
  const [fiscalSaved, setFiscalSaved] = useState(false);
  const [closedPeriods, setClosedPeriods] = useState<any[]>([]);

  const fetchClosedPeriods = () => {
    api('/bookkeeping/closed-periods').then((d: any) => setClosedPeriods(d.data || []));
  };

  // Fetch fiscal period and closed periods
  useEffect(() => {
    api('/bookkeeping/fiscal-period').then((d: any) => {
      if (d.fiscal_year_start) setFiscalStart(d.fiscal_year_start);
      if (d.fiscal_year_end) setFiscalEnd(d.fiscal_year_end);
    });
    fetchClosedPeriods();
  }, []);

  const saveBF = async (code: string) => {
    const val = parseFloat(bfEdits[code]);
    if (isNaN(val)) return;
    await api(`/bookkeeping/accounts/${code}`, { method: 'PATCH', body: { opening_balance: val } });
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    setBfEdits(prev => { const n = {...prev}; delete n[code]; return n; });
  };

  const saveFiscal = async () => {
    await api('/bookkeeping/fiscal-period', { method: 'PATCH', body: { fiscal_year_start: fiscalStart, fiscal_year_end: fiscalEnd } });
    setFiscalSaved(true);
    setTimeout(() => setFiscalSaved(false), 2000);
  };

  const grouped: Record<string, any[]> = {};
  for (const a of accounts) {
    const parent = a.parent_code || '__root__';
    if (!grouped[parent]) grouped[parent] = [];
    grouped[parent].push(a);
  }

  const topLevel = (code: string) => !accounts.find((a: any) => a.parent_code === code);

  return (
    <div className="space-y-4">
      {/* Fiscal period */}
      <div className="bg-card border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">財政年度 Fiscal Period</span>
        <span className="text-xs text-muted-foreground">起</span>
        <input type="month" value={fiscalStart} onChange={e => setFiscalStart(e.target.value)}
          className="px-2 py-1 border rounded text-sm bg-background" />
        <span className="text-xs text-muted-foreground">至</span>
        <input type="month" value={fiscalEnd} onChange={e => setFiscalEnd(e.target.value)}
          className="px-2 py-1 border rounded text-sm bg-background" />
        <button onClick={saveFiscal}
          className={`px-3 py-1 rounded text-xs font-medium ${fiscalSaved ? 'bg-green-100 text-green-700' : 'bg-primary text-primary-foreground hover:opacity-90'}`}>
          {fiscalSaved ? tr('✓ Saved', '✓ 已儲存', '✓ 已储存') : tr('Save', '儲存', '储存')}
        </button>
      </div>

      {/* Period & Year-End Actions */}
      <div className="bg-card border rounded-xl p-4 space-y-3">
        <span className="text-sm font-medium">會計操作 Actions</span>
        <div className="flex flex-wrap gap-2">
          <button onClick={async () => {
            const start = prompt(tr('Close period start (YYYY-MM-DD):', '關帳期間起 (YYYY-MM-DD)：', '关帐期间起 (YYYY-MM-DD)：'));
            const end = prompt(tr('Close period end (YYYY-MM-DD):', '關帳期間至 (YYYY-MM-DD)：', '关帐期间至 (YYYY-MM-DD)：'));
            if (!start || !end) return;
            await api('/bookkeeping/close-period', { method: 'POST', body: { period_start: start, period_end: end } });
            fetchClosedPeriods();
            toast.info(tr('Period closed', '已關帳', '已关帐'));
          }} className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded text-xs font-medium hover:bg-amber-200">
            {tr('Close Period', '關帳 Close Period', '关帐 Close Period')}
          </button>
          <button onClick={async () => {
            if (!confirm(tr(
              'Execute year-end close? This will transfer all revenue/expense accounts to retained earnings and update opening balances.',
              '確定要執行年結嗎？這會將所有收入/費用科目結轉至保留盈餘，並更新承上結餘。',
              '确定要执行年结吗？这会将所有收入/费用科目结转至保留盈余，并更新承上结余。',
            ))) return;
            const date = prompt(tr('Fiscal year end date (YYYY-MM-DD):', '財政年度結束日 (YYYY-MM-DD)：', '财政年度结束日 (YYYY-MM-DD)：'), fiscalEnd || '');
            if (!date) return;
            const res = await api('/bookkeeping/year-end-close', { method: 'POST', body: { fiscal_end_date: date } });
            toast.info(tr(
              `Year-end close complete!\nRevenue: HKD ${res.revenue?.toLocaleString()}\nExpenses: HKD ${res.expenses?.toLocaleString()}\nNet Income: HKD ${res.net_income?.toLocaleString()}`,
              `年結完成！\n收入：HKD ${res.revenue?.toLocaleString()}\n支出：HKD ${res.expenses?.toLocaleString()}\n淨利：HKD ${res.net_income?.toLocaleString()}`,
              `年结完成！\n收入：HKD ${res.revenue?.toLocaleString()}\n支出：HKD ${res.expenses?.toLocaleString()}\n净利：HKD ${res.net_income?.toLocaleString()}`,
            ));
            queryClient.invalidateQueries({ queryKey: ['entries'] });
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
          }} className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded text-xs font-medium hover:bg-blue-200">
            {tr('Year-End Close', '年結 Year-End Close', '年结 Year-End Close')}
          </button>
          <button onClick={async () => {
            if (!confirm(tr(
              'Calculate profits tax provision? (Default rate 16.5%, first $2M at 8.25%)',
              '確定要計算利得稅撥備嗎？（預設稅率 16.5%，首 $2M 為 8.25%）',
              '确定要计算利得税拨备吗？（预设税率 16.5%，首 $2M 为 8.25%）',
            ))) return;
            const date = prompt(tr('Fiscal year end date (YYYY-MM-DD):', '財政年度結束日 (YYYY-MM-DD)：', '财政年度结束日 (YYYY-MM-DD)：'), fiscalEnd || '');
            if (!date) return;
            const res = await api('/bookkeeping/profits-tax-provision', { method: 'POST', body: { fiscal_end_date: date } });
            toast.info(tr(
              `Profits tax provision complete!\nAssessable profit: HKD ${res.net_income?.toLocaleString()}\nTax: HKD ${res.tax_amount?.toLocaleString()}`,
              `利得稅撥備完成！\n應評稅利潤：HKD ${res.net_income?.toLocaleString()}\n稅款：HKD ${res.tax_amount?.toLocaleString()}`,
              `利得税拨备完成！\n应评税利润：HKD ${res.net_income?.toLocaleString()}\n税款：HKD ${res.tax_amount?.toLocaleString()}`,
            ));
            queryClient.invalidateQueries({ queryKey: ['entries'] });
          }} className="px-3 py-1.5 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200">
            {tr('Profits Tax Provision', '利得稅撥備 Tax Provision', '利得税拨备 Tax Provision')}
          </button>
        </div>
        {closedPeriods.length > 0 && (
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">{tr('Closed Periods', '已關帳期間 Closed Periods', '已关帐期间 Closed Periods')}</span>
            {closedPeriods.map((cp: any) => (
              <div key={cp.id} className="flex items-center justify-between bg-muted/30 rounded px-3 py-1.5">
                <span className="text-xs">{cp.period_start} ~ {cp.period_end}</span>
                <button onClick={async () => {
                  if (!confirm(tr(
                    `Reopen period ${cp.period_start} ~ ${cp.period_end}?`,
                    `確定要重開 ${cp.period_start} ~ ${cp.period_end} 的關帳嗎？`,
                    `确定要重开 ${cp.period_start} ~ ${cp.period_end} 的关帐吗？`,
                  ))) return;
                  await api(`/bookkeeping/close-period/${cp.id}`, { method: 'DELETE' });
                  fetchClosedPeriods();
                }} className="text-xs text-destructive hover:underline">{tr('Reopen', '重開 Reopen', '重开 Reopen')}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Accounts with B/F balance */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left p-3 w-[100px]">科目編號</th>
              <th className="text-left p-3">科目名稱</th>
              <th className="text-left p-3 w-[80px]">類別</th>
              <th className="text-right p-3 w-[180px]">承上結餘 B/F</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a: any) => {
              const isParent = grouped[a.account_code]?.length > 0;
              // Depth from zero-stripped stem ('10000'→0, '11000'→1, leaves→2)
              const indent = Math.max(0, stemOfCode(a.account_code || '').length - 1);
              const editing = !isParent && a.account_code in bfEdits;
              const bfVal = editing ? bfEdits[a.account_code] : (a.opening_balance || 0);
              return (
                <tr key={a.id} className={`border-b hover:bg-muted/30 ${isParent ? 'font-semibold bg-muted/20' : ''}`}>
                  <td className="p-3 font-mono text-xs" style={{paddingLeft: `${12 + indent * 16}px`}}>
                    {a.account_code}
                  </td>
                  <td className="p-3 truncate max-w-[300px]">{a.account_name}</td>
                  <td className="p-3 text-xs capitalize text-muted-foreground">{a.account_type}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number" step="0.01"
                        value={bfVal}
                        disabled={isParent}
                        onChange={e => setBfEdits(prev => ({...prev, [a.account_code]: e.target.value}))}
                        onKeyDown={e => { if (e.key === 'Enter') saveBF(a.account_code); }}
                        onBlur={() => { if (editing) saveBF(a.account_code); }}
                        title={isParent
                          ? tr('Group account — B/F balances are entered on its sub-accounts', '類別科目——承上結餘請填於其子科目', '类别科目——承上结余请填于其子科目')
                          : undefined}
                        className="w-32 px-2 py-1 border rounded text-xs text-right bg-background disabled:bg-transparent disabled:text-muted-foreground"
                      />
                      {editing && (
                        <button onClick={() => saveBF(a.account_code)}
                          className="p-1 text-primary hover:bg-primary/10 rounded">
                          <Save className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
